import { __assign } from 'tslib';
import { useBrand } from '@shared-ui/brand-context';
import { useExperiment } from '@shared-ui/experiment-context';
import * as React from 'react';
import { classNames, useUitkContext, validatePropType } from 'uitk-react-utils';
import { UitkIconThemes } from './types.esm.js';
import styles from './uitk-icons.css';
var getClassNameList = classNames.bind(styles);
var UitkIconSize;
(function (UitkIconSize) {
  UitkIconSize["EXTRA_SMALL"] = "xsmall";
  UitkIconSize["SMALL"] = "small";
  UitkIconSize["MEDIUM"] = "medium";
  UitkIconSize["LARGE"] = "large";
  UitkIconSize["XLARGE"] = "xlarge";
})(UitkIconSize || (UitkIconSize = {}));
var UitkIcon = function (props) {
  var _a;
  var baseCssClass = 'uitk-icon';
  var throwNameError = function (nameError) {
    typeof window !== 'undefined' && console.error("Invalid icon name prop: ".concat(nameError));
  };
  var className = props.className,
    description = props.description,
    id = props.id,
    name = props.name,
    size = props.size,
    theme = props.theme,
    title = props.title,
    shadow = props.shadow,
    spotlight = props.spotlight;
  var contextData = useUitkContext().data;
  var contextClassNames = (contextData !== null && contextData !== void 0 ? contextData : {}).contextClassNames;
  if (!name) {
    throwNameError(name);
  }
  if ((title || description) && !id && typeof window !== 'undefined') {
    console.error("Icon ".concat(name, " requires 'id' if 'title' or 'description' are provided"));
  }
  var dropShadowValues = {
    dx: 0,
    dy: 0,
    stdDeviation: 1
  };
  var uid = Math.random().toString(36).substring(7);
  var filterId = "shadow-filter".concat(id ? "-".concat(id) : "-".concat(uid));
  var domRef = function (b) {};
  var isDirectional = name && /_(?:back|forward|left|right|before|next|half)$/.test(name);
  // If spotlight isn't using the UitkIconSpotlightInterface, restructure it
  // to make working with the spotlight prop more predictable
  var spotLightIsObject = typeof spotlight === 'object';
  var newSpotlight = false;
  if (spotLightIsObject) {
    newSpotlight = spotlight;
  } else if (spotlight) {
    newSpotlight = {
      theme: spotlight,
      background: true
    };
  }
  var wrapperClassNames = getClassNameList(baseCssClass, className, contextClassNames === null || contextClassNames === void 0 ? void 0 : contextClassNames.UitkIcon, (_a = {}, _a["".concat(baseCssClass, "-directional")] = isDirectional, _a["".concat(baseCssClass, "-").concat(size)] = validatePropType(size, UitkIconSize) && !spotlight && size !== UitkIconSize.MEDIUM, _a["".concat(baseCssClass, "-spotlight-icon")] = newSpotlight, _a), theme && !spotlight && UitkIconThemes.get(theme));
  var spotlightIconContainerClassNames = getClassNameList(newSpotlight && ["".concat(baseCssClass, "-spotlight"), "".concat(baseCssClass, "-spotlight-").concat(newSpotlight.theme), newSpotlight.background && "".concat(baseCssClass, "-spotlight-has-background")]);
  var isSVGFEDropShadowElement = typeof SVGFEDropShadowElement === 'function';
  var style = shadow && isSVGFEDropShadowElement ? {
    filter: "url(#".concat(filterId, ")")
  } : undefined;
  var getIconPath = useBrand().getIconPath;
  var pathToRender = function (svgName) {
    var iconToRender = (svgName === null || svgName === void 0 ? void 0 : svgName.indexOf('icon__')) === 0 ? svgName.replace('icon__', '') : svgName;
    var iconPathString = getIconPath === null || getIconPath === void 0 ? void 0 : getIconPath(iconToRender);
    iconPathString || throwNameError(svgName);
    return iconPathString || '';
  };
  var svgNodes = [title && "<title id=\"".concat(id, "-title\">").concat(title, "</title>"), description && "<desc id=\"".concat(id, "-description\">").concat(description, "</desc>"), shadow && "<defs>\n      <filter id=\"".concat(filterId, "\">\n        <feDropShadow dx=\"").concat(dropShadowValues.dx, "\" dy=\"").concat(dropShadowValues.dy, "\" stdDeviation=\"").concat(dropShadowValues.stdDeviation, "\"/>\n      </filter>\n    </defs>")].filter(Boolean).join('');
  var a11yProps = {
    'aria-describedby': description ? "".concat(id, "-description") : undefined,
    'aria-hidden': title || description ? undefined : true,
    'aria-label': title || undefined,
    role: title || description ? 'img' : undefined
  };
  var iconProps = {
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg',
    xmlnsXlink: 'http://www.w3.org/1999/xlink'
  };
  var svgProps = __assign(__assign({
    ref: domRef,
    className: wrapperClassNames,
    style: style
  }, a11yProps), iconProps);
  var innerNodes = function (html) {
    if (html.length > 0) {
      return {
        dangerouslySetInnerHTML: {
          __html: html
        }
      };
    }
    return {};
  };
  var exposure = useExperiment('Bernie_SVG_delivery', true).exposure;
  var innerNodesMarkup = (exposure === null || exposure === void 0 ? void 0 : exposure.bucket) === 1 || (exposure === null || exposure === void 0 ? void 0 : exposure.bucket) === 2 ? "".concat(svgNodes, "<use href=\"#").concat(name, "\" />") : "".concat(svgNodes).concat(pathToRender(name));
  var iconMarkup = React.createElement("svg", __assign({}, svgProps, innerNodes(innerNodesMarkup)));
  return spotlight ? React.createElement("div", {
    className: spotlightIconContainerClassNames
  }, iconMarkup) : iconMarkup;
};
var getMetaData = function (metaDataProps) {
  if (!metaDataProps) {
    console.error('Must pass something');
    return false;
  }
  if (!metaDataProps.data) {
    console.error('Must pass name or data property');
    return false;
  }
  var icon = metaDataProps;
  var iconData = icon.data;
  var iconProps = __assign({}, metaDataProps);
  // Override attributes with custom ones
  var svgProps = __assign({
    role: 'icon',
    height: '24',
    viewBox: '0 0 24 24',
    width: '24',
    xmlns: 'http://www.w3.org/2000/svg'
  }, icon.attrs);
  Object.assign(svgProps, {
    'aria-hidden': true
  });
  return {
    iconData: iconData,
    iconProps: iconProps,
    svgProps: svgProps
  };
};
var attributize = function (obj) {
  return Object.keys(obj)
  // eslint-disable-next-line
  .reduce(function (str, attr) {
    return str += "".concat(attr, "=\"").concat(obj[attr], "\" ");
  }, '').trim();
};
var getMarkerPopup = function (markerProps) {
  var _a = getMetaData(markerProps),
    svgProps = _a.svgProps,
    iconData = _a.iconData;
  return "<svg ".concat(attributize(svgProps), ">").concat(iconData, "</svg>");
};
UitkIcon.defaultProps = {
  size: UitkIconSize.MEDIUM
};
UitkIcon.displayName = 'UitkIcon';
export { UitkIcon, UitkIconSize, attributize, getMarkerPopup, getMetaData };